import { Injectable, NgZone, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * TELLS AN OPEN TAB THAT A NEW BUILD HAS BEEN RELEASED (#45, release blocker, Tatenda "YES TO ALL RECS" 2026-10-26).
 *
 * A screen loaded before a release keeps running the old code until the browser reloads — and after the release the API
 * asks every payment for its currency and closes an examination a new way, so an old tab is refused on every money action
 * with nothing on the screen saying why. This compares the main bundle the tab is running with the one the server now
 * serves (index.html names it, with its content hash) and raises a notice when they differ. It never reloads by itself:
 * a half-filled form would be lost.
 *
 * Checked every few minutes and whenever the tab comes back into view. When it cannot tell — no hashed bundle (the dev
 * server, tests), the server unreachable, a maintenance page — it says nothing.
 */
@Injectable({
  providedIn: 'root',
})
export class AppVersionService {
  private static readonly CHECK_EVERY_MS = 5 * 60 * 1000;

  /** true once the server serves a newer build than this tab is running */
  readonly updateAvailable$ = new BehaviorSubject<boolean>(false);

  private loadedMain: string | null = null;
  private timer?: ReturnType<typeof setInterval>;

  constructor(@Optional() private ngZone?: NgZone) {}

  /** The hashed main bundle a page names (main.<hash>.js), or null. Pure. */
  static mainBundleIn(html: string): string | null {
    const match = /\bmain\.[0-9a-f]{8,}\.js\b/i.exec(html ?? '');
    return match ? match[0] : null;
  }

  /** Is the page the server serves a different build from the one loaded? Never "yes" when it cannot tell. Pure. */
  static isNewBuild(loadedMain: string | null, servedHtml: string): boolean {
    const served = AppVersionService.mainBundleIn(servedHtml);
    return loadedMain !== null && served !== null && served !== loadedMain;
  }

  start(): void {
    this.loadedMain = AppVersionService.mainBundleIn(
      Array.from(document.scripts).map((s) => s.getAttribute('src') ?? '').join(' ')
    );
    if (this.loadedMain === null || this.timer !== undefined) {
      return; // the dev server or a test runner: nothing to compare
    }
    const run = () => {
      this.timer = setInterval(() => this.check(), AppVersionService.CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', this.onVisible);
    };
    this.ngZone ? this.ngZone.runOutsideAngular(run) : run();
  }

  stop(): void {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  async check(): Promise<void> {
    if (this.updateAvailable$.value) {
      return;
    }
    try {
      const html = await this.fetchServed();
      if (AppVersionService.isNewBuild(this.loadedMain, html)) {
        this.ngZone ? this.ngZone.run(() => this.updateAvailable$.next(true)) : this.updateAvailable$.next(true);
      }
    } catch {
      // offline, or the site is in maintenance mid-release: say nothing and look again later
    }
  }

  reload(): void {
    window.location.reload();
  }

  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') {
      this.check();
    }
  };

  /** index.html as the server serves it now — never from a cache. */
  private fetchServed(): Promise<string> {
    const url = new URL('index.html', document.baseURI);
    url.searchParams.set('v', String(Date.now()));
    return fetch(url.toString(), { cache: 'no-store' }).then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))));
  }
}
